/* START_MODULE_CONTRACT
 * PURPOSE: Чистые помощники для работы с датами (нормализация, арифметика, форматирование ru-RU).
 * SCOPE: startOfDay/Month, addDays/Years, toIsoDate, isSameDay, isWeekend, форматтеры.
 * DEPENDS: нет
 * LINKS: M-FE-TIMELINE
 * ROLE: RUNTIME
 * MAP_MODE: EXPORTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * startOfDay - дата с обнулённым временем.
 * startOfMonth - первый день месяца в 00:00.
 * addDays / addYears - сдвиг даты.
 * toIsoDate - YYYY-MM-DD.
 * isSameDay - сравнение дат по календарному дню.
 * isWeekend - выходной ли день.
 * formatMonthYear / formatWeekdayShort / formatMonthShort / formatDayMonth - подписи ru-RU.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: function contracts и парные END_METHOD.
 * END_CHANGE_SUMMARY
 */

/* START_METHOD_startOfDay */
/* START_CONTRACT: startOfDay
 * PURPOSE: Вернуть копию даты с временем 00:00:00.000.
 * INPUTS: { date: Date|string|number }
 * OUTPUTS: { Date }
 * SIDE_EFFECTS: нет (не мутирует вход).
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: startOfDay
 */
export function startOfDay(date) {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}
/* END_METHOD_startOfDay */

/* START_METHOD_startOfMonth */
/* START_CONTRACT: startOfMonth
 * PURPOSE: Вернуть первый день месяца заданной даты в 00:00.
 * INPUTS: { date: Date }
 * OUTPUTS: { Date }
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: startOfMonth
 */
export function startOfMonth(date) {
  return startOfDay(new Date(date.getFullYear(), date.getMonth(), 1));
}
/* END_METHOD_startOfMonth */

/* START_METHOD_addDays */
/* START_CONTRACT: addDays
 * PURPOSE: Сдвинуть дату на N дней.
 * INPUTS: { date: Date, days: number }
 * OUTPUTS: { Date }
 * SIDE_EFFECTS: нет (не мутирует вход).
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: addDays
 */
export function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}
/* END_METHOD_addDays */

/* START_METHOD_addYears */
/* START_CONTRACT: addYears
 * PURPOSE: Сдвинуть дату на N лет.
 * INPUTS: { date: Date, years: number }
 * OUTPUTS: { Date }
 * SIDE_EFFECTS: нет (не мутирует вход).
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: addYears
 */
export function addYears(date, years) {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
}
/* END_METHOD_addYears */

/* START_METHOD_toIsoDate */
/* START_CONTRACT: toIsoDate
 * PURPOSE: Форматировать дату в строку YYYY-MM-DD (локальное время).
 * INPUTS: { date: Date }
 * OUTPUTS: { string }
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: toIsoDate
 */
export function toIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
/* END_METHOD_toIsoDate */

/* START_METHOD_isSameDay */
/* START_CONTRACT: isSameDay
 * PURPOSE: Сравнить две даты по календарному дню.
 * INPUTS: { a: Date, b: Date }
 * OUTPUTS: { boolean }
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: isSameDay
 */
export function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
  );
}
/* END_METHOD_isSameDay */

/* START_METHOD_isWeekend */
/* START_CONTRACT: isWeekend
 * PURPOSE: Проверить, является ли день субботой или воскресеньем.
 * INPUTS: { date: Date }
 * OUTPUTS: { boolean }
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: isWeekend
 */
export function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}
/* END_METHOD_isWeekend */

/* START_METHOD_formatMonthYear */
/* START_CONTRACT: formatMonthYear
 * PURPOSE: Подпись "месяц год" на ru-RU.
 * INPUTS: { date: Date }
 * OUTPUTS: { string }
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: formatMonthYear
 */
export function formatMonthYear(date) {
  return date.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}
/* END_METHOD_formatMonthYear */

/* START_METHOD_formatWeekdayShort */
/* START_CONTRACT: formatWeekdayShort
 * PURPOSE: Короткая подпись дня недели на ru-RU.
 * INPUTS: { date: Date }
 * OUTPUTS: { string }
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: formatWeekdayShort
 */
export function formatWeekdayShort(date) {
  return date.toLocaleDateString('ru-RU', { weekday: 'short' });
}
/* END_METHOD_formatWeekdayShort */

/* START_METHOD_formatMonthShort */
/* START_CONTRACT: formatMonthShort
 * PURPOSE: Короткая подпись месяца на ru-RU.
 * INPUTS: { date: Date }
 * OUTPUTS: { string }
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: formatMonthShort
 */
export function formatMonthShort(date) {
  return date.toLocaleDateString('ru-RU', { month: 'short' });
}
/* END_METHOD_formatMonthShort */

/* START_METHOD_formatDayMonth */
/* START_CONTRACT: formatDayMonth
 * PURPOSE: Подпись "ДД.ММ".
 * INPUTS: { date: Date }
 * OUTPUTS: { string }
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: formatDayMonth
 */
export function formatDayMonth(date) {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
}
/* END_METHOD_formatDayMonth */
